/**
 * OptimizerSequencer — DORMANT (DD-RO-BUGS3-0724 Item 5: ASSESSED, not deleted).
 *
 * Renders a single continuous whole-roster "move plan" TIMELINE (step #1 → #N with cumulative
 * running points/cap and a final projected-rank/cap summary) — a genuinely distinct visualization
 * that exists nowhere else. It is imported by OptimizerDashboard but never rendered; the Sequences
 * subview renders OptimizerTradeRecs instead.
 *
 * Status: its data contract diverged from the backend. It reads per-step `step.move_type /
 * description / players_in/out / step_pts_delta / cap_after / cumulative_pts` and top-level
 * `final_projected_rank / final_cap_remaining`, but `/optimizer/sequences`
 * (get_optimization_sequences) emits `trade_sequences[]` (trade-package objects), none of those
 * `step.*` fields — and had zero live callers. The underlying sequence_integrator output IS live,
 * surfaced by OptimizerTradeRecs (discrete trade cards). Wiring this back requires a backend
 * shape-adapter that flattens trade_sequences into atomic step objects + computes final rank/cap.
 * NOT redundant enough to delete (the timeline UX is unique) and NOT wireable as-is — left dormant
 * pending a product decision. See DD_RO_BUGS3_REPORT.md.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Loader, AlertCircle, ArrowRightLeft, UserPlus, UserMinus, TrendingUp, DollarSign, Trophy, Hash } from 'lucide-react';
import { dynastyTokens } from '../../../services/colorService';
import { leaguesAPI } from '../../../services/apiService';
import PlayerLink from '../../../components/PlayerLink';

const colors = dynastyTokens.colors;

const moveTypeConfig = {
  trade:  { label: 'Trade',  color: colors.warning, icon: ArrowRightLeft },
  pickup: { label: 'Pickup', color: colors.success, icon: UserPlus },
  drop:   { label: 'Drop',   color: colors.error,   icon: UserMinus },
};

const ordSuffix = (n) => {
  if (!n) return '—';
  const s = ['th','st','nd','rd'];
  const v = n % 100;
  return n + (s[(v-20)%10] || s[v] || s[0]);
};

const OptimizerSequencer = ({ leagueId, league, userTeam }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sequenceData, setSequenceData] = useState(null);
  const [mode, setMode] = useState('balanced');
  const [maxMoves, setMaxMoves] = useState(5);

  const loadSequences = useCallback(async () => {
    if (!userTeam?.team_id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await leaguesAPI.getOptimizerSequences(leagueId, userTeam.team_id, maxMoves, mode);
      setSequenceData(res);
    } catch (err) {
      console.error('Sequencer load error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load move sequences');
    } finally {
      setLoading(false);
    }
  }, [leagueId, userTeam?.team_id, maxMoves, mode]);

  useEffect(() => {
    loadSequences();
  }, [loadSequences]);

  const sequence = sequenceData?.sequence || [];
  const totalGain = sequenceData?.total_pts_gained || 0;
  const finalRank = sequenceData?.final_projected_rank;
  const finalCap = sequenceData?.final_cap_remaining;

  return (
    <div style={{
      background: colors.neutral[900], borderRadius: 8, border: `1px solid ${colors.neutral[700]}`,
      padding: 16, overflowY: 'auto', height: 'calc(100vh - 120px)', maxHeight: 920,
    }}>
      {/* Controls row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {['balanced', 'win_now', 'dynasty'].map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              style={{
                padding: '4px 12px', fontSize: 12, fontWeight: 600, borderRadius: 4,
                border: mode === m ? '1px solid #eab308' : `1px solid ${colors.neutral[700]}`,
                background: mode === m ? 'rgba(234,179,8,0.15)' : 'transparent',
                color: mode === m ? '#eab308' : colors.neutral[400],
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              {m === 'balanced' ? 'Balanced' : m === 'win_now' ? 'Win Now' : 'Dynasty'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: colors.neutral[500] }}>Max moves:</span>
          {[3, 5, 7, 10].map(n => (
            <button
              key={n}
              onClick={() => setMaxMoves(n)}
              style={{
                padding: '3px 8px', fontSize: 11, fontWeight: 600, borderRadius: 3,
                border: maxMoves === n ? '1px solid #eab308' : `1px solid ${colors.neutral[700]}`,
                background: maxMoves === n ? 'rgba(234,179,8,0.15)' : 'transparent',
                color: maxMoves === n ? '#eab308' : colors.neutral[400],
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >{n}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: colors.neutral[400] }}>
          <Loader style={{ width: 20, height: 20, marginRight: 8, animation: 'spin 1s linear infinite' }} />
          Computing optimal move sequence...
        </div>
      ) : error ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: colors.error, gap: 8 }}>
          <AlertCircle style={{ width: 20, height: 20 }} />
          {error}
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <SummaryCard
              icon={TrendingUp}
              label="Total Gain"
              value={totalGain > 0 ? `+${totalGain}` : String(totalGain)}
              valueColor={totalGain > 0 ? colors.success : totalGain < 0 ? colors.error : colors.neutral[400]}
              suffix="pts"
            />
            <SummaryCard
              icon={Trophy}
              label="Projected Rank"
              value={ordSuffix(finalRank)}
              valueColor={finalRank <= 3 ? colors.success : finalRank <= 7 ? colors.warning : colors.error}
            />
            <SummaryCard
              icon={DollarSign}
              label="Cap Remaining"
              value={finalCap != null ? `$${Math.round(finalCap)}` : '—'}
              valueColor={colors.white}
            />
            <SummaryCard
              icon={Hash}
              label="Moves"
              value={String(sequence.length)}
              valueColor={colors.white}
            />
          </div>

          {/* Timeline */}
          {sequence.length === 0 ? (
            <div style={{ textAlign: 'center', color: colors.neutral[500], fontSize: 13, padding: 40 }}>
              Your team is already optimized — no moves improve your standings.
            </div>
          ) : (
            <div style={{ position: 'relative', paddingLeft: 28 }}>
              {/* Vertical line */}
              <div style={{
                position: 'absolute', left: 9, top: 8, bottom: 8, width: 2,
                background: colors.neutral[700],
              }} />

              {sequence.map((step, idx) => {
                const cfg = moveTypeConfig[step.move_type] || moveTypeConfig.pickup;
                const Icon = cfg.icon;
                const ptsDelta = step.step_pts_delta || step.total_points_delta || 0;

                return (
                  <div key={idx} style={{ position: 'relative', marginBottom: 12 }}>
                    {/* Timeline dot */}
                    <div style={{
                      position: 'absolute', left: -28 + 4, top: 14,
                      width: 12, height: 12, borderRadius: '50%',
                      background: cfg.color, border: `2px solid ${colors.neutral[900]}`,
                      zIndex: 2,
                    }} />

                    {/* Step card */}
                    <div style={{
                      background: colors.neutral[800], border: `1px solid ${colors.neutral[700]}`,
                      borderRadius: 6, padding: '10px 14px',
                    }}>
                      {/* Header: step number + badge + description */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: colors.neutral[500] }}>#{step.step}</span>
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
                          background: `${cfg.color}20`, color: cfg.color,
                          display: 'flex', alignItems: 'center', gap: 3,
                        }}>
                          <Icon style={{ width: 10, height: 10 }} />
                          {cfg.label}
                        </span>
                        <span style={{ flex: 1, fontSize: 12, color: colors.white, fontWeight: 500 }}>
                          {step.description}
                        </span>
                      </div>

                      {/* Players involved */}
                      <div style={{ display: 'flex', gap: 16, fontSize: 11, marginBottom: 6 }}>
                        {step.players_out?.length > 0 && (
                          <div style={{ color: colors.neutral[400] }}>
                            <span style={{ color: colors.error, fontWeight: 600 }}>Out: </span>
                            {step.players_out.map((p, i) => (
                              <span key={p.ids?.mlb || i}>
                                {i > 0 && ', '}
                                <PlayerLink mlbId={p.ids?.mlb} name={p.info?.full_name || '—'} style={{ color: colors.neutral[300], fontSize: 11 }} />
                              </span>
                            ))}
                          </div>
                        )}
                        {step.players_in?.length > 0 && (
                          <div style={{ color: colors.neutral[400] }}>
                            <span style={{ color: colors.success, fontWeight: 600 }}>In: </span>
                            {step.players_in.map((p, i) => (
                              <span key={p.ids?.mlb || i}>
                                {i > 0 && ', '}
                                <PlayerLink mlbId={p.ids?.mlb} name={p.info?.full_name || '—'} style={{ color: colors.neutral[300], fontSize: 11 }} />
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Metrics row */}
                      <div style={{ display: 'flex', gap: 12, fontSize: 11 }}>
                        <span style={{ color: ptsDelta > 0 ? colors.success : ptsDelta < 0 ? colors.error : colors.neutral[500], fontWeight: 600 }}>
                          {ptsDelta > 0 ? `+${ptsDelta}` : ptsDelta} pts
                        </span>
                        {step.salary_impact != null && (
                          <span style={{ color: colors.neutral[400] }}>
                            {step.salary_impact >= 0 ? '+' : ''}{Math.round(step.salary_impact)} salary
                          </span>
                        )}
                        {step.cap_after != null && (
                          <span style={{ color: colors.neutral[400] }}>
                            ${Math.round(step.cap_after)} cap
                          </span>
                        )}
                      </div>

                      {/* Cumulative state */}
                      {step.cumulative_pts != null && (
                        <div style={{
                          marginTop: 6, paddingTop: 6,
                          borderTop: `1px solid ${colors.neutral[700]}`,
                          fontSize: 10, color: colors.neutral[500],
                        }}>
                          After: {step.cumulative_pts > 0 ? `+${step.cumulative_pts}` : step.cumulative_pts} pts total
                          {step.roster_count_after != null && ` · ${step.roster_count_after} roster`}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const SummaryCard = ({ icon: Icon, label, value, valueColor, suffix }) => (
  <div style={{
    flex: 1, background: colors.neutral[800], border: `1px solid ${colors.neutral[700]}`,
    borderRadius: 6, padding: '12px 14px',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
      <Icon style={{ width: 12, height: 12, color: colors.neutral[500] }} />
      <span style={{ fontSize: 11, color: colors.neutral[500], fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </span>
    </div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
      <span style={{ fontSize: 24, fontWeight: 700, color: valueColor }}>{value}</span>
      {suffix && <span style={{ fontSize: 11, color: colors.neutral[500] }}>{suffix}</span>}
    </div>
  </div>
);

export default OptimizerSequencer;
